import { communityJsonSource } from '@/container/community/communityJsonSource';
import { communityXmlSource } from '@/container/community/communityXmlSource';
import { useIsJsonFormEnabled, useIsXmlFormEnabled } from '@/features/flags';
import { useMutateFillFormDemo } from '@/hooks/mutation/useMutateFillFormDemo';
import { useMutateFillXmlFormDemo } from '@/hooks/mutation/useMutateFillXmlFormDemo';
import { type FillFormDemoResponse } from '@/types';
import { type DetailedPublicTemplateResponse } from '@/types/global-types';
import { useMutateFillJsonFormDemo } from '@heidi/templates/data';
import { isAxiosError } from 'axios';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { formFillDemoMessages } from './template-form-demo.messages';
import { type XmlFormFillResult } from './types';

interface UseFillFormSubmitArgs {
  template: DetailedPublicTemplateResponse;
  onSucceeded: (filledPdfUrl: string) => void;
  onXmlSucceeded: (result: XmlFormFillResult) => void;
  onJsonSucceeded: (fieldValues: Record<string, string>) => void;
  onFailed: (errorMessage: string) => void;
  onSubmit: () => void;
}

export interface UseFillFormSubmit {
  isPending: boolean;
  submit: (transcript: string) => void;
}

// Wraps the fill-form-demo mutation with trimming and 429-specific error mapping.
const mapFillError = (error: unknown, intl: ReturnType<typeof useIntl>): string => {
  const isRateLimited = isAxiosError(error) && error.response?.status === 429;
  return intl.formatMessage(
    isRateLimited
      ? formFillDemoMessages.errorRateLimited
      : formFillDemoMessages.errorGeneric
  );
};

export function useFillFormSubmit({
  template,
  onSucceeded,
  onXmlSucceeded,
  onJsonSucceeded,
  onFailed,
  onSubmit,
}: UseFillFormSubmitArgs): UseFillFormSubmit {
  const intl = useIntl();
  const isXmlFormEnabled = useIsXmlFormEnabled();
  const isJsonFormEnabled = useIsJsonFormEnabled();
  const { mutate: fillForm, isPending: isPdfPending } = useMutateFillFormDemo();
  const { mutate: fillXmlForm, isPending: isXmlPending } = useMutateFillXmlFormDemo();
  const { mutate: fillJsonForm, isPending: isJsonPending } = useMutateFillJsonFormDemo();

  const formFields = useMemo(
    () => template.template_content.form_fields ?? [],
    [template.template_content.form_fields]
  );
  const s3ObjectKey = template.template_content.s3_object_key;
  const xmlSource = communityXmlSource(template, isXmlFormEnabled);
  const jsonSource = communityJsonSource(template, isJsonFormEnabled);

  const submit = useCallback(
    (transcript: string) => {
      const trimmed = transcript.trim();
      if (!trimmed || formFields.length === 0) return;
      if (!xmlSource && !jsonSource && !s3ObjectKey) return;

      onSubmit();

      if (jsonSource) {
        fillJsonForm(
          {
            json_source: jsonSource,
            form_fields: formFields,
            transcript: trimmed,
            specialty_name: template.specialty,
            language: intl.locale,
          },
          {
            onSuccess: (data) => onJsonSucceeded(data.field_values),
            onError: (error: unknown) => onFailed(mapFillError(error, intl)),
          }
        );
        return;
      }

      if (xmlSource) {
        fillXmlForm(
          {
            xml_source: xmlSource,
            form_fields: formFields,
            transcript: trimmed,
            specialty_name: template.specialty,
            language: intl.locale,
          },
          {
            onSuccess: (data) => {
              onXmlSucceeded({
                filledXml: data.filled_xml,
                fieldValues: data.field_values,
              });
            },
            onError: (error: unknown) => {
              onFailed(mapFillError(error, intl));
            },
          }
        );
        return;
      }

      fillForm(
        {
          s3_object_key: s3ObjectKey ?? '',
          form_fields: formFields,
          transcript: trimmed,
          specialty_name: template.specialty,
          language: intl.locale,
        },
        {
          onSuccess: (data: FillFormDemoResponse) => {
            onSucceeded(
              `data:${data.filled_pdf_mime_type};base64,${data.filled_pdf_base64}`
            );
          },
          onError: (error: unknown) => {
            onFailed(mapFillError(error, intl));
          },
        }
      );
    },
    [
      s3ObjectKey,
      xmlSource,
      jsonSource,
      formFields,
      onSubmit,
      template.specialty,
      fillForm,
      fillXmlForm,
      fillJsonForm,
      intl,
      onSucceeded,
      onXmlSucceeded,
      onJsonSucceeded,
      onFailed,
    ]
  );

  return { isPending: isPdfPending || isXmlPending || isJsonPending, submit };
}
